/* eslint-disable no-console */
/**
 * Rockwell Automation Scripts.
 * Rockwell Automation scripts to be executed globally.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { isElementInViewport } from 'js/helpers.js';
import languages from '../i18n/translations.json';

const pageLanguage = document.querySelector('meta[property="language"]') ? document.querySelector('meta[property="language"]').content.substr(0, 2) : 'en';
const pageCountry = document.querySelector('meta[property="country"]') ? document.querySelector('meta[property="country"]').content : 'US';
const lang = (pageLanguage === 'zh') ? `${pageLanguage}_${document.documentElement.lang.substring(3, 5).toUpperCase()}` : pageLanguage;
const defaultOffset = 15;

// Display toast message after redirect for certain missing pages
const targetPathArray = ['blogs', 'case-studies', 'press-releases', 'trust-center/security-advisories', 'webinars', 'workforce-development-training'];
const filterPath = window.location.href;

async function checkToast(toastPath) {
    const referrerPath = document.referrer;
    if (referrerPath.includes(`/${toastPath}/`)) {
        let pageMissing = false;
        if (toastPath === 'trust-center/security-advisories') {
            const getResponse = await fetch(referrerPath, { method: 'GET' });
            const parser = new DOMParser();
            const pageContents = parser.parseFromString(await getResponse.text(), 'text/html');
            const heroElement = pageContents.querySelector('.hero-banner');
            pageMissing = heroElement === null;
        } else {
            const headResponse = await fetch(referrerPath, { method: 'HEAD' });
            pageMissing = headResponse.status >= 400 && headResponse.status < 600;
        }
        if (pageMissing) {
            const toastLanguage = languages[lang];
            const toastDuration = 6;
            const interval = setInterval(() => {
                const element = document.querySelector('.load-complete');
                if (element) {
                    clearInterval(interval);
                    switch (toastPath) {
                    case 'blogs':
                        showToast(toastLanguage.toastBlogs, toastDuration);
                        break;
                    case 'case-studies':
                        showToast(toastLanguage.toastCaseStudies, toastDuration);
                        break;
                    case 'press-releases':
                        showToast(toastLanguage.toastPressReleases, toastDuration);
                        break;
                    case 'trust-center/security-advisories':
                        showToast(toastLanguage.toastVulnerability, toastDuration);
                        break;
                    case 'webinars':
                        showToast(toastLanguage.toastWebinars, toastDuration);
                        break;
                    case 'workforce-development-training':
                        showToast(toastLanguage.toastTraining, toastDuration);
                        break;
                    default:
                        break;
                    }
                }
            }, 100);
        }
    }
}

const pathMatch = targetPathArray.find((path) => filterPath.includes(`/${path}.html`) || filterPath.includes(`/${path}/training-course-catalog.html`));
if (pathMatch) {
    checkToast(pathMatch);
}

function showToast(message, seconds) {
    const toast = document.createElement('div');
    toast.classList.add('toast');
    const rect = document.querySelector('.root').getBoundingClientRect();
    toast.style.top = `${rect.top + 16}px`;
    toast.innerHTML = `<div class='toast-icon'></div><div class='toast-message'>${message}</div>`;
    document.body.appendChild(toast);

    setTimeout(() => {
        toast.classList.add('hide');
        setTimeout(() => {
            toast.remove();
        }, 1000);
    }, seconds * 1000);
}

function getAnchorOffset(anchorElem, extraOffset = 0) {
    const alertBanner = document.querySelector('.alert-banner');
    const alertBannerOffset = alertBanner ? alertBanner.offsetHeight : 0;

    const globalNavOriginal = document.querySelector('header[class$="-global-nav"]');
    const globalNavOriginalOffset = globalNavOriginal ? globalNavOriginal.offsetHeight : 0;

    const mainHeader = document.querySelector('div[class$="__main-header"]');
    const mainHeaderOffset = mainHeader ? mainHeader.offsetHeight : 0;

    const raHeader = document.querySelector('ra-header');
    const raHeaderOffset = raHeader ? raHeader.offsetHeight : 0;

    const globalNavV2 = document.querySelector('ra-global-nav-v2');
    const globalNavV2Offset = globalNavV2 ? globalNavV2.offsetHeight : 0;
    const globalNavPlex = document.querySelector('plex-global-nav');
    const globalNavOffset = globalNavPlex ? globalNavPlex.offsetHeight : globalNavV2Offset;

    const subNav = document.querySelector('.sub-nav');
    const subNavOffset = subNav ? subNav.offsetHeight : 0;

    const maxOffset = Math.max(globalNavOriginalOffset, mainHeaderOffset, raHeaderOffset, globalNavOffset);

    const navOffset = subNavOffset !== 0 ? subNavOffset : maxOffset;

    const hashEleRect = anchorElem.getBoundingClientRect();
    const hashYPos = hashEleRect.top + (window.pageYOffset || document.documentElement.scrollTop);

    let finalScrollPos = hashYPos - (alertBannerOffset + navOffset + defaultOffset + extraOffset);
    if (subNav && maxOffset > 0) {
        finalScrollPos -= maxOffset;
    }

    return finalScrollPos;
}

export function scrollToAnchor (anchorElem, extraOffset = 0) {
    // If the anchor is within a tab, activate that tab
    const containingTab = anchorElem.closest('.cmp-tabs__tabpanel');
    if (containingTab) {
        const containingTabIndex = [...containingTab.closest('.cmp-tabs').querySelectorAll('.tab-container')].findIndex((tabContainer) => tabContainer === containingTab.closest('.tab-container'));

        [...containingTab.closest('.cmp-tabs').querySelector('.cmp-tabs__tablist').querySelectorAll('.cmp-tabs__tab')].forEach((tab) => {
            if (tab.classList.contains('cmp-tabs__tab--active')) {
                tab.classList.remove('cmp-tabs__tab--active');
                tab.setAttribute('aria-selected', false);
            }
        });
        [...containingTab.closest('.cmp-tabs').querySelectorAll('.cmp-tabs__tabpanel')].forEach((tab) => {
            if (tab.classList.contains('cmp-tabs__tabpanel--active')) {
                tab.classList.remove('cmp-tabs__tabpanel--active');
                tab.setAttribute('aria-hidden', true);
            }
        });
        [...containingTab.closest('.cmp-tabs').querySelector('.cmp-tabs__tablist').querySelectorAll('.cmp-tabs__tab')][containingTabIndex].classList.add('cmp-tabs__tab--active');
        [...containingTab.closest('.cmp-tabs').querySelector('.cmp-tabs__tablist').querySelectorAll('.cmp-tabs__tab')][containingTabIndex].setAttribute('aria-selected', true);
        containingTab.classList.add('cmp-tabs__tabpanel--active');
        containingTab.setAttribute('aria-hidden', false);
    }

    const finalScrollPos = getAnchorOffset(anchorElem, extraOffset);
    window.scrollTo({
        top: finalScrollPos,
        left: 0,
        behavior: 'smooth'
    });
}

function enablePDFViewer(pdfURL) {
    const pdfViewerDisabled = document.getElementById(
        'pira-pdf-viewer-disabled'
    );
    const pdfContainer = document.querySelector('.pdf-viewer__container');

    let isExternal = true;
    try {
        if (pdfURL.includes(window.location.hostname) || pdfURL.includes('rockwellautomation.com') || pdfURL.startsWith('/content/dam/')) {
            isExternal = false;
        }
    } catch {
        // do nothing
    }

    if (pdfViewerDisabled || !pdfContainer || isExternal) {
        window.open(pdfURL, '_blank');
        return;
    }

    // replace http links with https
    pdfURL = pdfURL.replace(/^http:/, 'https:'); // eslint-disable-line no-param-reassign

    document.getElementById(
        'cmp-pdf-viewer-container'
    ).innerHTML = `<iframe class="pdf-viewer__iframe" src="/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${pdfURL}" ></iframe>`;
    document.body.style.overflow = 'hidden';

    // open viewer
    setTimeout(() => {
        pdfContainer.style.display = 'block';
    }, 100);

    // intercept 'local/RA' PDF links within Viewer and also open in Viewer instead of new tab
    function iFrameClick() {
        pdfContainer.querySelector('.pdf-viewer__iframe').contentWindow.document.body.onclick = (ev) => {
            ev.preventDefault();
            const originalHref = ev.target.tagName === 'A' ? ev.target.getAttribute('href') : '';
            if (originalHref && !originalHref.startsWith('#')) {
                if (originalHref.includes('.pdf') && originalHref.includes('rockwellautomation.com')) {
                    const pdfIframe = pdfContainer.querySelector('.pdf-viewer__iframe');
                    const originalHrefHttps = originalHref.replace(/^http:/, 'https:');
                    pdfIframe.src = `/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${originalHrefHttps}`;
                } else {
                    window.open(originalHref, '_blank');
                }
            }
        };
    }

    function closeLogic() {
        pdfContainer.style.display = 'none';
        document.body.style.overflow = 'auto';
        document.getElementById('cmp-pdf-viewer-container').innerHTML = '';
        pdfContainer.removeEventListener('click', closeLogic);
        window.dispatchEvent(new CustomEvent('hide-loading'));
    }

    pdfContainer.addEventListener('click', closeLogic);
    pdfContainer.querySelector('.pdf-viewer__iframe').addEventListener('load', iFrameClick);
}

window.piraEnablePDFViewer = enablePDFViewer;

/**
 * Custom link behavior
 */
document.body.addEventListener('click', async (ev) => {
    /** @type {HTMLAnchorElement?} */
    const anchor = ev.target?.closest('a');
    if (!anchor) {
        return;
    }
    const originalHref = anchor.href;
    const hrefAttr = anchor.getAttribute('href');
    if (!originalHref) {
        return;
    }

    // In-page anchor links (outside sub-nav)
    if (hrefAttr.substring(0, 1) === '#' && !/sub-nav/.test(anchor.className)) {
        const linkHashOrig = document.getElementById(hrefAttr.substring(1));
        const linkHashElem = linkHashOrig !== null
            ? linkHashOrig
            : document.getElementById(decodeURI(hrefAttr.substring(1)));
        if (linkHashElem) {
            ev.preventDefault();
            scrollToAnchor(linkHashElem);
            return;
        }

        // Drift chat links
        if (hrefAttr.startsWith('#dbt--')) {
            const cookiePopup = document.body.querySelector('.cookie-popup');
            if ((!window.drift || !window.drift.on || !window.getPiraObj?.().performanceCookies) && cookiePopup) {
                ev.preventDefault();
                cookiePopup.style.removeProperty('display');
                cookiePopup.classList.add('active');
                cookiePopup.querySelector('.cookie-popup__performance-cookies').classList.add('required');
                document.body.classList.add('no-scroll');
            }
            return;
        }
    }

    if (/\.pdf(\?.+)?$/.test(originalHref)) {
    // Links on non-EN pages, pointing to Lit Lib, that are not within AB documentation resource tables
        ev.preventDefault();
        ev.stopPropagation();
        const pdfLanguage = pageCountry === 'TW' ? 'zc' : pageLanguage;
        if (pdfLanguage !== 'en' && originalHref.match(/.+literature\.rockwellautomation\.com.+/) && !(anchor.parentNode.getAttribute('data-label') === 'Language')) {
            const translatedPdf = originalHref.replace(/^https?(.+-)([a-z]{2})(_[A-Z]{2})?(-.*?\.pdf.*)$/, `https$1${pdfLanguage}$4`);
            const englishPdf = originalHref.replace(/^https?(.+-)([a-z]{2})(_[A-Z]{2})?(-.*?\.pdf.*)$/, 'https$1en$4');
            try {
                const response = await fetch(translatedPdf, { method: 'HEAD' });
                if (!response.ok) {
                    throw new Error();
                }
                enablePDFViewer(translatedPdf); // If translated PDF exists, open it
            } catch {
                enablePDFViewer(englishPdf); // Otherwise open English PDF
            }
        } else {
            enablePDFViewer(originalHref);
        }
        // Click anywhere on the page to close the PDF viewer.
    } else if (
        pageLanguage !== 'en'
        && originalHref.match(/^.*rockwell-?automation.*\/(articles|blogs|campaigns|case-studies|legal(-notices)?|podcasts|presentations|press-releases|webinars|support\/documentation\/(overview|technical-data))\/.*$/)
    ) {
        // Modify links to certain 'publication/campaign' pages
        ev.preventDefault();
        ev.stopPropagation();
        try {
            const response = await fetch(originalHref, { method: 'HEAD' });
            if (!response.ok) {
                throw new Error();
            }
            // Continue to valid link destination
            window.location.href = originalHref;
        } catch {
            // Otherwise display Cancel/Continue dialog
            if (document.getElementsByClassName('dialog_content').length === 0) {
                const language = languages[lang];
                const dialog = document.createElement('div');
                dialog.className = 'overlay';
                dialog.innerHTML = `
                    <div class='dialog_content'>
                        <p style='font-weight:bold'>${language.enOnlyDialog}</p>
                        <p>${language.newWindow}</p>
                        <div class='dialog_buttons'>
                            <span id='btn_cancel'>${language.btnCancel}</span>
                            <span id='btn_continue'>${language.btnContinue}</span>
                        </div>
                    </div>`;
                document.body.appendChild(dialog);
                dialog.addEventListener('click', () => {
                    dialog.remove();
                });
                document.getElementById('btn_continue').addEventListener('click', async () => {
                    let fallbackSite = 'en-us';
                    if (/^(CN|JP|KR|TW)$/.test(pageCountry)) {
                        fallbackSite = 'en-sg'; // non-EN APAC countries
                    } else if (/^(AT|BE|CH|DE|ES|FR|IT|PT)$/.test(pageCountry)) {
                        fallbackSite = 'en-gb'; // non-EN EMEA countries
                    } else if (/^(AR|BR|CO|MX|PR)$/.test(pageCountry)) {
                        fallbackSite = 'en-pr'; // non-EN LATAM countries
                    }

                    const enRegionalPage = originalHref.replace(/^(.+?)?(\/(articles|blogs|campaigns|case-studies|company|events|legal|news|podcasts|presentations|press-releases|webinars|support).+)$/, `https://${window.location.hostname.replace('.cn', '')}/${fallbackSite}$2`);
                    const enUsPage = originalHref.replace(/^(.+?)?(\/(articles|blogs|campaigns|case-studies|company|events|legal|news|podcasts|presentations|press-releases|webinars|support).+)$/, `https://${window.location.hostname.replace('.cn', '')}/en-us$2`);
                    dialog.remove();

                    if (fallbackSite === 'en-us') {
                        window.open(enUsPage, '_blank'); // Open page on EN-US site
                    } else {
                        try {
                            const response = await fetch(enRegionalPage, { method: 'HEAD' }); // If page exists on regional EN site
                            if (!response.ok) {
                                throw new Error();
                            }
                            window.open(enRegionalPage, '_blank'); // Then open page on regional EN site
                        } catch {
                            window.open(enUsPage, '_blank'); // Otherwise open page on EN-US site
                        }
                    }
                });
            }
        }
    }
});

// Add icon if link opens new tab
document.querySelectorAll('a').forEach((anchor) => {
    const { href, target } = anchor;
    if (href && target && target !== '_self') {
        // Also add text to Title attr
        const newText = `${languages[lang].targetBlank}`;
        let linkTitle = anchor.getAttribute('title');
        if (linkTitle == null || linkTitle === '') {
            linkTitle = `${newText}`;
        } else if (!linkTitle.includes(newText)) {
            linkTitle = `${linkTitle} (${newText})`;
        }
        anchor.setAttribute('title', linkTitle);

        // But DON'T add icon for Cards, Images, Product Config Assistant Buttons, Social Links or PDF links
        const parentClasses = anchor.parentNode.className;
        const isExcludedParent = /image|launch-assistant|-meteor/.test(parentClasses);
        const isExcludedNode = /card|social-links/.test(anchor.className);
        const isPdfLink = /\.pdf/.test(href);

        if (!(isExcludedParent || isExcludedNode || isPdfLink)) {
            if (/quick-links__item/.test(parentClasses)) {
                // eslint-disable-next-line no-param-reassign
                anchor.querySelector('.quick-links__label').innerHTML += '<div class="new-tab-icon"></div>';
            } else {
                // eslint-disable-next-line no-param-reassign
                anchor.innerHTML += '<div class="new-tab-icon"></div>';
            }
        }
    }
});

window.onload = () => {
    if (window.location.hash) {
        const urlHash = window.location.hash.replace('#', '');
        const urlHashElem = document.getElementById(urlHash);
        if (urlHashElem) {
            // Interim solution until DMX-3334 is completed
            window.setTimeout(() => {
                scrollToAnchor(urlHashElem);
            });
        }
    }
};

// ==== ABM card functionality =====

/* The other half of this logic is on launch, under the "ABM" rule. */

const abmStorageDurationMinutes = 1440; // 1 day

const abmDataStorageKey = 'pira_abm';
const abmTeaserClosedStorageKey = 'pira_abm_teaser_close';

/**
 * @param {string} key
 * @param {any} data
 * @param {number} durationMinutes
 */
function setTransientLocalStorageItem(key, data, durationMinutes) {
    window.localStorage.setItem(key, JSON.stringify({ data, expires: Date.now() + (durationMinutes * 60000) }));
}

/**
 * Attempts to load a JSON stringified local storage item of the shape
 * `{ expires: number, data: any }`. Removes expired items.
 *
 * @param {string} key
 * @returns {any | null} The data if it is present and not expired, else null.
 */
function getTransientLocalStorageItem(key) {
    try {
        const value = window.localStorage.getItem(key);
        if (value) {
            const { expires, data } = JSON.parse(value);
            if (expires) {
                if (expires > Date.now()) {
                    return data;
                }
                window.localStorage.removeItem(key);
            } else {
                console.error(`LocalStorage item '${key}' is missing 'expires' field.`);
            }
        }
    } catch {
        console.error(`Failed to load local storage item: ${key}`);
    }
    return null;
}

function countryToCampaignRegion(country) {
    if (!country) {
        return null;
    }
    const countyLowerCase = country.toLowerCase();
    const countryMap = {
        na: ['us'],
        eu: ['at', 'be', 'cz', 'dk', 'fi', 'fr', 'de', 'hu', 'ie', 'il', 'it', 'nl', 'no', 'pl', 'pt', 'ro', 'ru', 'es', 'se', 'ch', 'tr', 'ua', 'gb'],
        ame: ['me', 'za', 'ao', 'bh', 'bw', 'eg', 'sz', 'gh', 'iq', 'jo', 'ke', 'kw', 'lb', 'ls', 'mg', 'mw', 'mu', 'mz', 'na', 'ng', 'om', 'qa', 'sa', 'tz', 'ug', 'ae', 'ye', 'zm', 'zw'],
        latam: ['ar', 'co', 'pr', 'bz', 'bo', 'br', 'cl', 'cr', 'do', 'ec', 'sv', 'gf', 'gt', 'gy', 'ht', 'hn', 'mx', 'ni', 'pa', 'py', 'pe', 'sr', 've'],
        apac: ['au', 'cn', 'in', 'id', 'jp', 'kr', 'my', 'nz', 'ph', 'sg', 'tw', 'th', 'vn'],
    };
    const campaignMap = {
        na: ['na'],
        apac: ['apac'],
        emea: ['eu', 'ame'],
        la: ['latam'],
    };

    function checkMapping(target, mapping) {
        return Object.keys(mapping).find((key) => mapping[key].includes(target));
    }

    const region = checkMapping(countyLowerCase, countryMap);
    const campaignRegion = checkMapping(region, campaignMap);
    return campaignRegion;
}

function piraGetConfigPageLocation(relativeConfigPath) {
    const CONTENT_PATH = '/content/rockwell-automation';

    let path = window.location.pathname.toLowerCase();
    let isAuthor = false;
    if (path.startsWith(CONTENT_PATH)) {
        isAuthor = true;
        path = path.slice(CONTENT_PATH.length);
    }

    let language = null;
    let region = null;
    if (path.startsWith('/campaigns')) {
        const match = path.match(/\/campaigns\/([^/]+)\/([^/]+)\/.*/i);
        if (match) {
            [, region, language] = match;
        }
    } else {
        const [docLang, docCountry] = document.documentElement.lang?.split('-') || [];
        language = docLang || document.querySelector('meta[property="language"]')?.content;
        const country = docCountry || document.querySelector('meta[property="country"]')?.content;
        region = countryToCampaignRegion(country);
    }
    if (!language || !region) {
        return null;
    }
    let configPagePath = `/campaigns/${region}/${language}/${relativeConfigPath}`;
    if (isAuthor) {
        configPagePath = `${CONTENT_PATH}${configPagePath}?wcmmode=disabled`;
    }
    return configPagePath;
}
window.piraGetConfigPageLocation = piraGetConfigPageLocation;

function piraGetAbmConfig(urlParam = null, abmConfigUrl = null) {
    let abmConfigPath = abmConfigUrl;
    if (!abmConfigPath) {
        abmConfigPath = piraGetConfigPageLocation('config-pages/abm-teaser.abm.json');
    }
    if (!abmConfigPath) {
        return null;
    }
    if (urlParam) {
        abmConfigPath = `${abmConfigPath}?${urlParam}`;
    }
    return fetch(abmConfigPath)
        .then((response) => response.json())
        .catch(() => { console.error('cannot get ABM config'); });
}
window.piraGetAbmConfig = piraGetAbmConfig;

function setSelectorAttribute({
    parent = document,
    selector,
    displayCondition,
    attributeName,
    attributeValue,
}) {
    const element = parent.querySelector(selector);
    if (element) {
        if (displayCondition) {
            element.setAttribute(attributeName, attributeValue);
            return true;
        }
        element.style.display = 'none';
    }
    return false;
}

function hexToString(hex) {
    // Remove any leading zeros (if necessary)
    const newHex = hex.replace(/^0+/, '');

    // Split the hex string into 2-character chunks (1 byte each)
    let result = '';
    for (let i = 0; i < newHex.length; i += 2) {
        // Parse each pair of characters as a hexadecimal number
        const charCode = parseInt(newHex.substr(i, 2), 16);
        // Convert the number back to its ASCII character
        result += String.fromCharCode(charCode);
    }
    return result.slice(6);
}

// Decode entire salesContact JSON from hex to string
function salesContactDecode(salesContact) {
    const newSalesContact = {};
    Object.entries(salesContact).forEach(([key, value]) => {
        const stringKey = hexToString(key);
        const stringValue = hexToString(value);

        newSalesContact[stringKey] = stringValue;
    });
    return newSalesContact;
}

function piraShowABMSalesContact(salesContactUrl = null) {
    let salesContactPageLocation = salesContactUrl;

    window.piraHideABMTeaser();
    window.piraHideABMSalesContact();

    const piraAbmCookie = getTransientLocalStorageItem(abmDataStorageKey);
    if (!piraAbmCookie) {
        console.debug('pira_abm cookie not found. Do nothing...');
    }

    let { salesContact } = piraAbmCookie;
    if (!salesContact) {
        console.debug('pira sales contact not found. Do nothing...');
    }

    if (!salesContactPageLocation) {
        salesContactPageLocation = piraGetConfigPageLocation('config-pages/abm-sales-contact/_jcr_content/root/main-par/sales_contact_teaser.html');
    }
    if (!salesContactPageLocation) {
        return;
    }

    salesContact = salesContactDecode(salesContact);

    fetch(salesContactPageLocation)
        .then((response) => response.text())
        .then((data) => {
            const salesContactTeaser = document.createElement('div');
            salesContactTeaser.innerHTML = data;
            const wrapper = salesContactTeaser.querySelector('.cmp-sales-contact-teaser');
            salesContactTeaser.querySelector('.cmp-sales-contact-teaser__name').innerText = salesContact.name;
            salesContactTeaser.querySelector('.cmp-sales-contact-teaser__title').innerText = salesContact.title;
            const { phoneNumber } = salesContact;
            if (phoneNumber && phoneNumber !== '') {
                salesContactTeaser.querySelector('.cmp-sales-contact-teaser__phone-number').innerHTML = `${languages[lang].call}: <a href='tel:${phoneNumber.replace(/[^+\d]/g, '')}'>${phoneNumber}</a>`;
            }

            setSelectorAttribute({
                parent: salesContactTeaser,
                selector: '.cmp-sales-contact-teaser__image',
                displayCondition: salesContact.image,
                attributeName: 'src',
                attributeValue: `${salesContact.image}/_jcr_content/renditions/cq5dam.thumbnail.319.319.png`,
            });

            if (!salesContact.email && !salesContact.linkedin) {
                salesContactTeaser.querySelector('.cmp-sales-contact-teaser__connect').style.display = 'none';
            } else {
                setSelectorAttribute({
                    parent: salesContactTeaser,
                    selector: '.cmp-sales-contact-teaser__email',
                    displayCondition: salesContact.email,
                    attributeName: 'href',
                    attributeValue: `mailto:${salesContact.email}`,
                });
                setSelectorAttribute({
                    parent: salesContactTeaser,
                    selector: '.cmp-sales-contact-teaser__linkedin',
                    displayCondition: salesContact.linkedin,
                    attributeName: 'href',
                    attributeValue: salesContact.linkedin,
                });
            }

            let gateId = salesContact.gatedFormId;
            if (gateId && gateId.startsWith('gate-')) {
                gateId = `#${gateId}`;
            }
            if (setSelectorAttribute({
                parent: salesContactTeaser,
                selector: '.cmp-sales-contact-teaser__cta',
                displayCondition: gateId && gateId.startsWith('#gate-'),
                attributeName: 'href',
                attributeValue: gateId,
            })) {
                const gatedContentName = window.GatedContentObject || 'gcdc';
                const gatedContentObject = window[gatedContentName];
                if (gatedContentObject) {
                    gatedContentObject('loadGates');
                }
            }

            wrapper.classList.add('hidden');
            document.body.appendChild(salesContactTeaser);
            setTimeout(() => {
                wrapper.classList.remove('hidden');
            }, 0);
        })
        .catch(() => { console.error('cannot get ABM sales contact page'); });
}
window.piraShowABMSalesContact = piraShowABMSalesContact;

function piraShowABMTeaser(visitorType, abmTeaserUrl = null) {
    let abmTeaserLocation = abmTeaserUrl;

    window.piraHideABMTeaser();
    window.piraHideABMSalesContact();

    const company = getTransientLocalStorageItem(abmDataStorageKey);
    if (!company) {
        console.debug('pira_abm cookie not found. Do nothing...');
    }

    if (!abmTeaserLocation) {
        abmTeaserLocation = piraGetConfigPageLocation('config-pages/abm-teaser/_jcr_content/root/main-par/abm_teaser.html');
    }
    if (!abmTeaserLocation) {
        return;
    }
    fetch(abmTeaserLocation)
        .then((response) => response.text())
        .then((data) => {
            const abmTeaser = document.createElement('div');
            abmTeaser.innerHTML = data;
            const wrapper = abmTeaser.querySelector('.cmp-abm-teaser');

            function substituteCompany(elem) {
                const text = elem.innerText.replaceAll('{company}', company.label);
                const thisElem = elem;
                thisElem.innerText = text;
            }

            substituteCompany(abmTeaser.querySelector('.cmp-abm-teaser__title'));

            let selectorToHide;
            let selectorToShow;
            if (visitorType === 'welcome') {
                selectorToShow = '.cmp-abm-teaser__welcome-text';
                selectorToHide = '.cmp-abm-teaser__returning-text';
            } else {
                selectorToShow = '.cmp-abm-teaser__returning-text';
                selectorToHide = '.cmp-abm-teaser__welcome-text';
            }

            substituteCompany(abmTeaser.querySelector(selectorToShow));
            abmTeaser.querySelector(selectorToHide).style.display = 'none';

            // TODO: Account for /campaigns links in mapping
            let companyLink = company.link;
            if (companyLink?.startsWith('/content/rockwell-automation/campaigns')) {
                companyLink = companyLink.replace('/content/rockwell-automation/campaigns', '/campaigns');
            }

            setSelectorAttribute({
                parent: abmTeaser,
                selector: '.cmp-abm-teaser__cta',
                displayCondition: companyLink,
                attributeName: 'href',
                attributeValue: companyLink,
            });

            wrapper.classList.add('hidden');
            document.querySelector('.ra-global-nav-v2__main-header').appendChild(abmTeaser);
            setTimeout(() => {
                wrapper.classList.remove('hidden');
            }, 0);

            abmTeaser.querySelector('.cmp-abm-teaser__close').addEventListener('click', () => {
                wrapper.classList.add('hidden');
                setTransientLocalStorageItem(abmTeaserClosedStorageKey, true, abmStorageDurationMinutes);
            });
        })
        .catch(() => { console.error('cannot get ABM teaser'); });
}
window.piraShowABMWelcome = (abmTeaserUrl) => piraShowABMTeaser('welcome', abmTeaserUrl);
window.piraShowABMReturn = (abmTeaserUrl) => piraShowABMTeaser('return', abmTeaserUrl);

function hideABMCard(selector) {
    const teaser = document.querySelector(selector);
    if (teaser) {
        const wrapper = teaser.parentNode;
        wrapper.remove();
    }
}
window.piraHideABMSalesContact = () => hideABMCard('.cmp-sales-contact-teaser');
window.piraHideABMTeaser = () => hideABMCard('.cmp-abm-teaser');

// Scroll Animation
gsap.registerPlugin(ScrollTrigger);
document.querySelectorAll('.enable-animations .fade-up').forEach((elem) => {
    gsap.from(elem, {
        scrollTrigger: {
            trigger: elem,
        },
        duration: 0.5,
        opacity: 0,
        y: 50
    });
});

document.querySelectorAll('.enable-animations .fade-down').forEach((elem) => {
    gsap.from(elem, {
        scrollTrigger: {
            trigger: elem,
        },
        duration: 0.5,
        opacity: 0,
        y: -50
    });
});

document.querySelectorAll('.enable-animations .fade-left').forEach((elem) => {
    gsap.from(elem, {
        scrollTrigger: {
            trigger: elem,
        },
        duration: 0.5,
        opacity: 0,
        x: -50
    });
});

document.querySelectorAll('.enable-animations .fade-right').forEach((elem) => {
    gsap.from(elem, {
        scrollTrigger: {
            trigger: elem,
        },
        duration: 0.5,
        opacity: 0,
        x: 50
    });
});

// Scroll listener for Value Prop animation
const valuePropTargets = document.querySelectorAll('.value-prop .count-up');
if (valuePropTargets.length > 0) {
    window.addEventListener('scroll', animateValueProp);
}

function animateValueProp() {
    if (valuePropTargets.length > 0) {
        valuePropTargets.forEach((item) => {
            if (isElementInViewport(item) && item.classList.contains('count-up')) {
                const valuePropItem = item;
                const valuePropNum = valuePropItem.innerText.replace(/^\D*([\d.]+).*$/, '$1');
                if (Number.isNaN(valuePropNum) || !/\d/.test(valuePropNum)) return;
                const valuePropPrefix = valuePropItem.innerText.replace(/^(\D*)[\d.]+.*$/, '$1');
                const valuePropSuffix = valuePropItem.innerText.replace(/^.*[\d.]+(.*)$/, '$1');
                const decimalIndex = valuePropNum.indexOf('.');
                const decimalPlaces = decimalIndex >= 0 ? valuePropNum.length - decimalIndex - 1 : 0;
                const initTime = performance.now();
                const interval = setInterval(() => {
                    let t = (performance.now() - initTime) / 2000;
                    if (t >= 1) t = 1;
                    const currentVal = t * valuePropNum;
                    const pageLocale = pageLanguage.concat('-', pageCountry === '__' || pageCountry === 'MDE' ? 'US' : pageCountry);
                    const displayVal = Number(currentVal).toLocaleString(pageLocale, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
                    valuePropItem.innerText = valuePropPrefix.concat(displayVal, valuePropSuffix);
                    if (t >= 1) {
                        clearInterval(interval);
                    }
                }, 50);
                valuePropItem.classList.remove('count-up');
            }
        });
    }
}

// Hide elements when within binge experience iframe
try {
    if (window.frameElement?.classList.contains('binge-experience__iframe')) {
        const style = document.createElement('style');
        style.innerHTML = `
            .breadcrumb__nav, header, ra-header, ra-footer, .floating-button, ._hj_feedback_container, .uz_popup_container, #onetrust-consent-sdk, .fly-in {
                display: none !important;
            }
        `;
        document.body.append(style);

        // Find and remove "Subscribe" teasers
        document.querySelectorAll('.teaser a[href*="/company/about-us/email-preferences"]').forEach((teaserEmailLink) => {
            teaserEmailLink.closest('.teaser')?.remove(); // eslint-disable-line no-unused-expressions
        });
    }
} catch {
    // do nothing
}

// Repeat redirect functionality
if (!window.location.hostname.includes('adobe')) {
    const repeatRedirectEl = document.querySelector('.repeat-redirect-target');
    if (repeatRedirectEl && repeatRedirectEl.dataset.target) {
        const storageKey = `repeat-redirect-visited__${window.location.pathname}`;
        if (getTransientLocalStorageItem(storageKey)) {
            window.location.href = repeatRedirectEl.dataset.target;
        } else {
            setTransientLocalStorageItem(storageKey, true, 525960); // 1 year
        }
    }
}
